import React, { useState, useRef, useEffect } from 'react';
import { useJurisdictionSearch } from '@/hooks/useJurisdictionSearch';
import { Place } from '@/contexts/PlaceNamesContext';
import { formatPlaceName } from '@/lib/utils';

export interface HeroSectionProps {
  selectedPlace: Place | null;
  onPlaceSelect: (place: Place) => void;
  displayName: string;
  resultsContainerRef?: React.RefObject<HTMLDivElement>;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  selectedPlace,
  onPlaceSelect,
  displayName,
  resultsContainerRef
}) => {
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const featuredBenchmarks = [
    { text: 'Newly Permitted Units', icon: 'design_services' },
    { text: 'Existing Housing Stock', icon: 'home_work' },
    { text: 'Housing Stock Growth', icon: 'bar_chart_4_bars' },
    { text: 'Housing Availability & Affordability', icon: 'house' },
    { text: 'Rental Availability & Affordability', icon: 'apartment' },
  ];

  const {
    searchInputValue,
    setSearchInputValue,
    placeOptions,
    isSearching,
    isDebouncing,
    searchError,
    clearResults,
  } = useJurisdictionSearch(showAutocomplete);

  // Add logging to track state changes
  useEffect(() => {
    console.log('State update:', {
      searchInputValue,
      isEditing,
      selectedPlace: selectedPlace?.name,
      displayName
    });
  }, [searchInputValue, isEditing, selectedPlace, displayName]);

  // Update effect to handle selectedPlace changes
  useEffect(() => {
    console.log('Selected place changed:', {
      selectedPlace: selectedPlace?.name,
      displayName
    });
    
    if (selectedPlace && displayName) {
      setSearchInputValue(displayName);
      setIsEditing(false);
    } else {
      setSearchInputValue('');
      setIsEditing(false);
    }
  }, [selectedPlace, displayName]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    console.log('Input changing:', value);
    setIsEditing(true);
    setSearchInputValue(value);
    setShowAutocomplete(true);
  };

  const handleInputFocus = () => {
    console.log('Input focused');
    setShowAutocomplete(true);
  };

  const handleInputBlur = () => {
    console.log('Input blurred');
    setTimeout(() => {
      setIsEditing(false);
      setShowAutocomplete(false);
      clearResults();
    }, 150);
  };

  const handleSelectItem = (place: Place & { displayName: string }) => {
    console.log('Selecting new place:', {
      newPlace: place.name,
      displayName: place.displayName
    });
    
    // Reset search state
    setSearchInputValue('');
    setIsEditing(false);
    setShowAutocomplete(false);
    clearResults();
    
    // Call parent handler to update selectedPlace and displayName
    onPlaceSelect(place);
    
    // Blur input and scroll
    inputRef.current?.blur();
    setTimeout(() => {
      resultsContainerRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowAutocomplete(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <section className="w-full py-16 pb-12 sm:pb-16 md:pb-20 bg-gradient-to-br from-[#CA0079] to-[#F1AB11] mt-0">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 2xl:px-32">
        <h1 className="text-white text-3xl sm:text-4xl md:text-5xl font-bold leading-[1.3] font-geist">
          Permit Benchmarking
        </h1>
        <div className="mt-6 sm:mt-8 md:mt-[30px]">
          <p className="text-white text-xl sm:text-2xl md:text-[32px] font-semibold leading-[1.3] sm:leading-[42px] font-geist">
            Populations grow. Cities need to also. See how population, housing
            and permitting are keeping up or falling behind in any jurisdiction.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 w-full gap-6 lg:gap-8 xl:gap-12 mt-8 sm:mt-12 md:mt-[60px]">
          <div className="lg:col-start-1">
            <h2 className="text-white font-geist-mono text-base font-semibold tracking-[0.32px] uppercase leading-[130%]">
              Featured Benchmarks
            </h2>
            <ul className="flex w-full flex-col text-lg sm:text-xl text-white font-normal mt-5 space-y-4 font-geist">
              {featuredBenchmarks.map((benchmark, index) => (
                <li
                  key={index}
                  className="flex items-center gap-3"
                >
                  <span className="material-symbols-outlined w-6 h-6 text-2xl flex-shrink-0">
                    {benchmark.icon}
                  </span>
                  <span>{benchmark.text}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-start-2">
            <h2 className="text-white font-geist-mono text-base font-semibold tracking-[0.32px] uppercase leading-[130%]">
              get started here
            </h2>
            <div className="w-full text-lg sm:text-xl text-[#CA0079] font-normal mt-5 relative font-geist">
              <label htmlFor="jurisdiction-search" className="sr-only">
                Select your jurisdiction
              </label>
              <div className="relative">
                <input
                  ref={inputRef}
                  id="jurisdiction-search"
                  type="text"
                  value={isEditing ? searchInputValue : displayName}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  aria-autocomplete="list"
                  className="text-[#CA0079] bg-white w-full px-4 sm:px-6 py-3 sm:py-4 rounded-[11px] focus:outline-none focus:ring-2 focus:ring-[#CA0079] text-base sm:text-lg pl-12 sm:pl-14 font-geist"
                />
                
                {/* Persistent magnifying glass icon */}
                <div className="absolute left-4 sm:left-6 top-1/2 transform -translate-y-1/2 pointer-events-none flex items-center h-full">
                  <span className="material-symbols-outlined text-[#CA0079] text-xl opacity-50 leading-none">
                    search
                  </span>
                </div>

                {/* Placeholder text */}
                {!searchInputValue && !displayName && (
                  <div className="absolute left-12 sm:left-14 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <span className="text-[#CA0079] text-base sm:text-lg uppercase font-medium opacity-50 font-geist">
                      Select your jurisdiction
                    </span>
                  </div>
                )}
              </div>

              {/* Autocomplete dropdown */}
              {showAutocomplete && placeOptions.length > 0 && (
                <div 
                  ref={dropdownRef}
                  className="absolute top-full left-0 right-0 mt-2 bg-white rounded-[11px] shadow-lg border border-gray-200 z-50 max-h-60 overflow-y-auto"
                >
                  {placeOptions.map((place) => (
                    <button
                      key={place.place_fips}
                      onClick={() => handleSelectItem(place)}
                      className="w-full text-left px-4 sm:px-6 py-3 text-[#212121] hover:bg-gray-50 transition-colors text-base sm:text-lg first:rounded-t-[11px] last:rounded-b-[11px] font-geist"
                    >
                      {place.displayName}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Loading state */}
              {isDebouncing && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-[11px] shadow-lg border border-gray-200 z-50 p-4 text-center text-[#CA0079]">
                  Searching...
                </div>
              )}
              
              {/* Error state */}
              {searchError && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-[11px] shadow-lg border border-red-200 z-50 p-4 text-center text-red-500">
                  {searchError}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
